import { useEffect, useState } from 'react';
import { useAuth, useLanguage } from '@/contexts';
import { API_URLS } from '@/constants';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  teamSize?: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
}

export const useTeamWaitlistFormData = (onSuccess: () => void) => {
  const { user, isLoggedIn } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    teamSize: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | '';
    message: string;
  }>({ type: '', message: '' });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const validEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email);

    if (!formData.firstName.trim()) newErrors.firstName = t('waitlist.firstNameRequired');
    if (!formData.lastName.trim()) newErrors.lastName = t('waitlist.lastNameRequired');
    if (!formData.company.trim()) newErrors.company = t('waitlist.companyRequired');
    if (!formData.email.trim()) {
      newErrors.email = t('waitlist.emailRequired');
    } else if (!validEmail) {
      newErrors.email = t('waitlist.invalidEmail');
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch(API_URLS.WAITLIST.CREATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          setSubmitStatus({ type: 'error', message: t('waitlist.emailAlready') });
        } else {
          setSubmitStatus({ type: 'error', message: data.message || t('unexpectedError') });
        }
      } else {
        setSubmitStatus({ type: 'success', message: t('waitlist.emailSuccess') });
        setFormData({ firstName: '', lastName: '', email: '', company: '', teamSize: '' });
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: t('networkError') });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn && user) {
      const nameParts = user.name ? user.name.split(' ') : ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      setFormData(prev => ({
        ...prev,
        firstName,
        lastName,
        email: user.email || '',
      }));
    }
  }, [isLoggedIn, user]);

  return {
    formData,
    formErrors,
    isSubmitting,
    submitStatus,
    t,
    handleChange,
    handleSubmit,
  };
};
